import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getIdToken } from "@/lib/firebase";
import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { Link } from "wouter";

interface NotificationButtonProps {
  className?: string;
  iconClassName?: string;
  badgeClassName?: string;
  buttonClassName?: string;
}

async function fetchUnreadCount() {
  const token = await getIdToken();
  if (!token) return 0;

  const response = await fetch("/api/notifications/unread-count", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) return 0;

  const payload = await response.json();
  return typeof payload.count === "number" ? payload.count : 0;
}

export function NotificationButton({
  className,
  iconClassName = "h-5 w-5",
  badgeClassName,
  buttonClassName,
}: NotificationButtonProps) {
  const { isAuthenticated } = useAuth();

  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: ["/api/notifications/unread-count"],
    enabled: isAuthenticated,
    queryFn: fetchUnreadCount,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const displayCount = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <Link href="/notifications" className={className}>
      <Button
        variant="ghost"
        size="icon"
        className={`relative ${buttonClassName || ""}`}
        data-testid="button-notifications"
        aria-label={
          unreadCount > 0
            ? `알림 ${displayCount}개 읽지 않음`
            : "알림"
        }
      >
        <Bell className={iconClassName} />
        {unreadCount > 0 && (
          <span
            className={`absolute -right-1 -top-1 min-w-4 rounded-full bg-red-600 px-1 text-[10px] font-bold leading-4 text-white ${badgeClassName || ""}`}
          >
            {displayCount}
          </span>
        )}
      </Button>
    </Link>
  );
}
